import type { User } from '@gmn-portal/backend';
import {
  Box,
  FileText,
  Folder,
  Helicopter,
  List,
  Settings,
  Shield,
  Sliders,
  Users,
  Wrench,
} from 'lucide-react';
import type { RouteProp } from '@/shared/types/route-prop';
import {
  canReadService,
  isCustomerDomain,
  isGmnDomain,
  resolveUserGroup,
} from '@/shared/utils/permissions';

export const customerRoutes: RouteProp[] = [
  {
    to: '/c/my-operations',
    icon: Wrench,
    label: 'navigation.items.customerOperations',
    service: 'operations',
  },
  {
    to: '/c/contract-management/contracts',
    icon: FileText,
    label: 'navigation.items.customerContracts',
    service: 'contracts',
  },
  {
    to: '/c/objects',
    icon: Box,
    label: 'navigation.items.customerObjects',
    service: 'objects',
    children: [
      {
        to: '/c/objects',
        icon: List,
        label: 'navigation.items.allObjects',
        service: 'objects',
      },
    ],
    dynamicChildren: 'object-categories',
  },
  {
    to: '/c/my-customer/customer',
    icon: Users,
    label: 'navigation.items.customerProfile',
    service: 'customers',
  },
];

export const createCustomerRoutes = (user: User | null) =>
  filterRoutesByPermissions(customerRoutes, user);

export const gmnRoutes: RouteProp[] = [
  {
    to: '/gmn/contract-management/contracts',
    icon: FileText,
    label: 'navigation.items.gmnContracts',
    service: 'contracts',
  },
  {
    to: '/gmn/operations',
    icon: Wrench,
    label: 'navigation.items.gmnOperations',
    service: 'operations',
  },
  {
    to: '/gmn/objects',
    icon: Box,
    label: 'navigation.items.gmnObjects',
    service: 'objects',
    children: [
      {
        to: '/gmn/objects',
        icon: List,
        label: 'navigation.items.allObjects',
        service: 'objects',
      },
    ],
    dynamicChildren: 'object-categories',
  },
  {
    to: '/gmn/customer-management/customers',
    icon: Users,
    label: 'navigation.items.gmnCustomers',
    service: 'customers',
  },
  {
    to: '/gmn/customer-management/helicopter-service-providers',
    icon: Helicopter,
    label: 'navigation.items.gmnHelicopterServiceProviders',
    service: 'customers',
  },
  {
    to: '/gmn/settings',
    icon: Settings,
    label: 'navigation.items.gmnSettings',
    children: [
      {
        to: '/gmn/settings/user-groups',
        icon: Shield,
        label: 'navigation.items.gmnUserGroups',
        service: 'users',
        allowedGroupKeys: ['gmn-admin'],
      },
      {
        to: '/gmn/settings/users',
        icon: Users,
        label: 'navigation.items.gmnUsers',
        service: 'users',
        allowedGroupKeys: ['gmn-admin'],
      },
      {
        to: '/gmn/settings/object-categories',
        icon: Folder,
        label: 'navigation.items.gmnObjectCategories',
        service: 'object-categories',
        allowedGroupKeys: ['gmn-admin'],
      },
      {
        to: '/gmn/settings/metadata',
        icon: Sliders,
        label: 'navigation.items.gmnMetadata',
        service: 'contracts',
        allowedGroupKeys: ['gmn-admin'],
      },
    ],
  },
];

export const createGmnRoutes = (user: User | null) => filterRoutesByPermissions(gmnRoutes, user);

const filterRoutesByPermissions = (routes: RouteProp[], user: User | null): RouteProp[] => {
  const group = resolveUserGroup(user);
  if (!group) return [];
  const groupKey = group.key;

  const filter = (items: RouteProp[]): RouteProp[] => {
    return items.reduce<RouteProp[]>((acc, route) => {
      if (route.allowedGroupKeys?.length && !route.allowedGroupKeys.includes(groupKey)) {
        return acc;
      }
      const hasPermission = route.service ? canReadService(group, route.service) : true;
      const children = route.children ? filter(route.children) : undefined;
      const hasChildren = Boolean(children && children.length > 0);

      if (route.children && !hasChildren && !route.service) {
        return acc;
      }

      if (!hasPermission && !hasChildren) {
        return acc;
      }

      acc.push({
        ...route,
        children: hasChildren ? children : undefined,
      });
      return acc;
    }, []);
  };

  return filter(routes);
};

const pickFirstRoutePath = (routes: RouteProp[]): string | null => {
  for (const route of routes) {
    if (route.children && route.children.length > 0) {
      const childPath = pickFirstRoutePath(route.children);
      return childPath ?? route.to;
    }
    return route.to;
  }
  return null;
};

export const getDefaultRouteForUser = (user: User | null): string => {
  if (!user) return '/auth/login';

  const group = resolveUserGroup(user);
  if (!group) return '/auth/login';

  const isGmn = isGmnDomain(user);
  const isCustomer = isCustomerDomain(user);

  const routes = isGmn
    ? filterRoutesByPermissions(gmnRoutes, user)
    : filterRoutesByPermissions(customerRoutes, user);

  const isCustomerAdmin = isCustomer && group.key === 'customer-admin';
  const servicePriority = isGmn
    ? [
        'contracts',
        'operations',
        'objects',
        'customers',
        'users',
        'object-categories',
        'notifications',
      ]
    : isCustomerAdmin
      ? ['customers', 'operations', 'objects', 'contracts', 'notifications']
      : ['operations', 'objects', 'contracts', 'customers', 'notifications'];

  for (const service of servicePriority) {
    const match = routes.find((route) => route.service === service);
    if (match) {
      if (match.children && match.children.length > 0) {
        return pickFirstRoutePath(match.children) ?? match.to;
      }
      return match.to;
    }
  }

  return pickFirstRoutePath(routes) ?? (isCustomer ? '/c' : '/gmn');
};
